import React, { useState } from "react";
import { motion } from "framer-motion";
import { Copy, Check, Mail, ExternalLink, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";

const ease = [0.16, 1, 0.3, 1];
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.6, ease, delay },
});

const EMAIL_TEMPLATE = `Subject: Application – [Your Name] | [Target Role]

Hi ApplyStrat team,

I'd like to work with you. Here are my details:

━━━ ABOUT ME ━━━
Name:       [Full Name]
Email:      [Your Email Address]
Phone:      [+91 / +1 Phone Number]
LinkedIn:   https://linkedin.com/in/[your-profile]
Location:   [City, Country]

━━━ WHAT I'M TARGETING ━━━
Target Roles:       [e.g., Machine Learning Engineer, AI Engineer, Backend SDE]
Years of Experience:[e.g., 4 years]
Target Companies:   [e.g., Google, Amazon, Meta — or "any top-tier tech"]

━━━ MY REQUIREMENTS ━━━
[ ] H1B / OPT visa sponsorship required
[ ] Open to relocation — preferred cities: [City 1, City 2]
[ ] Remote only / Hybrid preferred
[ ] Expected package: [₹X LPA / $X USD]
[ ] Other: [Any additional preferences or constraints]

━━━ BRIEF INTRODUCTION ━━━
[2–3 sentences: your background, what you've built, what you're looking for]

I have attached my updated resume to this email.

Looking forward to working with you,
[Your Name]`;

const STEPS = [
  { num: "01", title: "Copy the email template", desc: "Use the template below — fill in your details, target roles, and requirements." },
  { num: "02", title: "Attach your resume", desc: "Attach your latest resume as a PDF. Name the file: FirstName_LastName_Resume.pdf" },
  { num: "03", title: "Send to hello@applystrat.com", desc: "We review every application within 24 hours and respond by email." },
];

export default function ApplyPage() {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(EMAIL_TEMPLATE);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div data-testid="apply-page" className="bg-white">
      <SEOHead
        title="Apply to Work With ApplyStrat | Tech Job Application Support"
        description="Apply to work with ApplyStrat. Email us your resume and requirements. We review every application within 24 hours."
        canonical="https://applystrat.com/apply"
      />

      {/* ── HERO ── */}
      <section className="pt-36 pb-14 bg-white text-center" style={{ background: "radial-gradient(circle at 50% -20%, rgba(255,82,0,0.12) 0%, transparent 65%)" }}>
        <div className="max-w-3xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease }}>
            <p className="section-label-orange mb-4">Apply</p>
            <h1 className="text-5xl md:text-6xl font-medium tracking-tighter text-ink mb-5 leading-tight">
              Apply to Work<br /><span className="text-brand">With Us</span>
            </h1>
            <p className="text-lg text-ink-secondary max-w-xl mx-auto leading-relaxed">
              We keep things simple. No forms. Just send your resume and details directly to our team — we respond within 24 hours.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── 3 STEPS ── */}
      <section className="py-16 bg-surface-raised" data-testid="apply-steps">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {STEPS.map((s, i) => (
              <motion.div key={s.num} {...fadeUp(i * 0.1)}
                className="bg-white rounded-3xl p-7 shadow-sm border border-black/5">
                <span className="text-4xl font-medium text-brand/20 block mb-4">{s.num}</span>
                <h3 className="text-ink font-medium text-base mb-2">{s.title}</h3>
                <p className="text-ink-secondary text-sm leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── EMAIL + TEMPLATE ── */}
      <section className="py-20 bg-white" data-testid="apply-email-section">
        <div className="max-w-3xl mx-auto px-6">

          {/* Direct email CTA */}
          <motion.div {...fadeUp()} className="text-center mb-14">
            <p className="text-ink-secondary text-sm uppercase tracking-widest font-medium mb-4">Send your application to</p>
            <Button asChild size="lg" className="hover:scale-[1.02] shadow-lg text-lg">
              <a href="mailto:hello@applystrat.com?subject=Application – [Your Name] | [Target Role]"
                data-testid="apply-email-link">
                <Mail size={22} />
                hello@applystrat.com
              </a>
            </Button>
            <p className="text-ink-secondary text-xs mt-4">Clicking the button opens your email client with the subject pre-filled.</p>
          </motion.div>

          {/* Template section */}
          <motion.div {...fadeUp(0.1)}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-medium text-ink">Email Template</h2>
                <p className="text-ink-secondary text-sm mt-1">Copy this, fill in your details, and send with your resume attached.</p>
              </div>
              <button onClick={handleCopy} data-testid="copy-email-template"
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${copied ? "bg-green-500 text-white" : "bg-brand text-white hover:bg-brand-hover"}`}>
                {copied ? <><Check size={14} /> Copied!</> : <><Copy size={14} /> Copy Template</>}
              </button>
            </div>

            {/* Template block */}
            <div className="bg-ink rounded-3xl overflow-hidden shadow-xl" data-testid="email-template-block">
              <div className="flex items-center gap-2 px-6 py-4 border-b border-white/10">
                <div className="flex gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-red-400/60" />
                  <span className="w-3 h-3 rounded-full bg-yellow-400/60" />
                  <span className="w-3 h-3 rounded-full bg-green-400/60" />
                </div>
                <span className="text-white/40 text-xs font-mono ml-2">application-template.txt</span>
              </div>
              <pre className="text-green-300/80 text-xs font-mono leading-relaxed p-6 whitespace-pre-wrap overflow-x-auto">
                {EMAIL_TEMPLATE}
              </pre>
            </div>
          </motion.div>

          {/* What happens next */}
          <motion.div {...fadeUp(0.2)} className="mt-10 p-6 rounded-2xl bg-brand-light border border-brand/15">
            <h3 className="text-ink font-medium text-sm mb-3">What happens after you send?</h3>
            <ul className="space-y-2">
              {[
                "We review your profile within 24 hours",
                "If it's a fit, we'll reply with next steps and onboarding details",
                "If we're full or it's not the right fit, we'll let you know honestly",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-ink-secondary text-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand mt-2 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Strategy call alternative */}
          <motion.div {...fadeUp(0.3)} className="mt-8 text-center space-y-3">
            <p className="text-ink-secondary text-sm mb-3">Prefer to talk first?</p>
            <a href="https://chat.whatsapp.com/DgSVsQclxdU3vPBxoffPY7?mode=gi_t" target="_blank" rel="noopener noreferrer" data-testid="apply-whatsapp-chat"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#25D366] text-white font-medium text-sm hover:bg-[#1ebe5d] transition-all shadow-sm">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Reach Out on WhatsApp
            </a>
          </motion.div>
        </div>
      </section>

      {/* ── FAQ STRIP ── */}
      <section className="py-14 bg-surface-raised">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div {...fadeUp()}>
            <p className="text-ink-secondary text-sm">Have questions before applying?</p>
            <p className="text-ink-secondary text-sm mt-1">
              Read the{" "}
              <a href="/faq" className="text-brand hover:text-brand-hover font-medium transition-colors">FAQ</a>
              {" "}or email us at{" "}
              <a href="mailto:hello@applystrat.com" className="text-brand hover:text-brand-hover font-medium transition-colors">hello@applystrat.com</a>
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
